import type { AtlasRecord, MapDefinition } from '@grs-atlas/shared';
import { US_STATE_NAMES, type UsStateCode } from '@grs-atlas/shared';

interface DetailPanelProps {
  map: MapDefinition;
  stateCode?: string;
  stateMetrics?: { recordCount: number; label?: string };
  records: AtlasRecord[];
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (record: AtlasRecord) => void;
  onViewHistory: (record: AtlasRecord) => void;
  onArchive: (record: AtlasRecord) => void;
}

export function DetailPanel({
  map,
  stateCode,
  stateMetrics,
  records,
  canEdit,
  onAdd,
  onEdit,
  onViewHistory,
  onArchive,
}: DetailPanelProps) {
  const detailKeys = map.detailFields.length
    ? map.detailFields
    : map.fields.map((f) => f.key);

  const title = stateCode
    ? `${US_STATE_NAMES[stateCode as UsStateCode] ?? stateCode}`
    : 'All matching records';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <h2 style={{ margin: '0 0 0.25rem' }}>{title}</h2>
          <p style={{ margin: 0, color: 'var(--muted)' }}>
            {stateMetrics
              ? `${stateMetrics.recordCount} ${map.recordLabel.toLowerCase()}${stateMetrics.recordCount === 1 ? '' : 's'}${stateMetrics.label ? ` · ${stateMetrics.label}` : ''}`
              : `${records.length} records`}
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn" onClick={onAdd} style={{ flexShrink: 0 }}>
            Add {map.recordLabel}
          </button>
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0' }}>
        {records.map((r) => (
          <li
            key={r.recordId}
            className="panel"
            style={{ padding: '0.85rem', marginBottom: '0.75rem' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <strong>{r.name}</strong>
              <span className="badge">{r.status}</span>
            </div>
            <dl style={{ margin: '0.5rem 0 0', display: 'grid', gap: '0.25rem' }}>
              {detailKeys.map((key) => {
                if (key === 'name') return null;
                const field = map.fields.find((f) => f.key === key);
                const label = field?.label ?? key;
                let value: unknown =
                  key === 'stateCodes'
                    ? r.stateCodes.join(', ')
                    : key === 'tags'
                      ? r.tags.join(', ')
                      : r.fields[key];
                if (Array.isArray(value)) value = value.join(', ');
                if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
                if (value == null || value === '') return null;
                return (
                  <div
                    key={key}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
                      gap: 8,
                    }}
                  >
                    <dt style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>{label}</dt>
                    <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{String(value)}</dd>
                  </div>
                );
              })}
            </dl>
            {canEdit && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn secondary" onClick={() => onEdit(r)}>
                  Edit
                </button>
                <button type="button" className="btn secondary" onClick={() => onViewHistory(r)}>
                  History
                </button>
                {r.status === 'active' && (
                  <button type="button" className="btn danger" onClick={() => onArchive(r)}>
                    Archive
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
        {!records.length && <li className="empty">No records for this selection.</li>}
      </ul>
    </div>
  );
}
